import { useRef, useState, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, Paperclip, Trash2, X } from 'lucide-react';
import { api } from '../../services/api';
import { toast } from '../../store/toastStore';
import { apiErrorMessage } from '../sales/shared';
import ConfirmDialog from '../ui/ConfirmDialog';
import { formatDate } from '../../utils/format';
import type { TicketAttachment } from '../../types';

/** The types the server accepts for a task's file (ticket_attachments). */
export const TASK_FILE_ACCEPT = 'image/jpeg,image/png,image/gif,image/webp,application/pdf,'
  + 'application/vnd.openxmlformats-officedocument.wordprocessingml.document,'
  + 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/zip';

const sizeText = (bytes: number) => (bytes >= 1048576 ? `${(bytes / 1048576).toFixed(1)}MB` : `${Math.max(1, Math.round(bytes / 1024))}KB`);

/**
 * P-06/A (D-44, D-57) — "📎 צירוף קבצים" of the task form: pick any number
 * of files from the computer; they travel with the create request (one call).
 */
export function TaskFilesPicker({ files, onChange }: { files: File[]; onChange: (files: File[]) => void }) {
  const { t } = useTranslation();
  const ref = useRef<HTMLInputElement>(null);
  const add = (e: ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(e.target.files ?? []);
    if (picked.length) onChange([...files, ...picked]);
    e.target.value = '';
  };
  return (
    <div className="space-y-2">
      <p className="label" id="task-files-label">{t('taskFiles.attach')}</p>
      <input ref={ref} type="file" multiple accept={TASK_FILE_ACCEPT} className="sr-only" aria-labelledby="task-files-label" onChange={add} data-testid="task-files-input" />
      <button type="button" className="btn btn-secondary btn-sm" onClick={() => ref.current?.click()}>
        <Paperclip size={14} aria-hidden="true" /> {t('taskFiles.choose')}
      </button>
      {files.length > 0 && (
        <ul className="space-y-1" aria-label={t('taskFiles.chosen')}>
          {files.map((f, i) => (
            <li key={`${f.name}-${i}`} className="flex items-center gap-2 text-sm text-text">
              <Paperclip size={12} className="shrink-0 text-text-2" aria-hidden="true" />
              <bdi className="min-w-0 flex-1 truncate">{f.name}</bdi>
              <span className="text-xs text-text-2 tabular-nums">{sizeText(f.size)}</span>
              <button
                type="button"
                className="btn-icon text-text-2 hover:text-text"
                aria-label={t('taskFiles.remove', { name: f.name })}
                onClick={() => onChange(files.filter((_, j) => j !== i))}
              >
                <X size={14} aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Upload several files to an existing task (each a request; the server stores them on the task). */
export async function uploadTaskFiles(ticketId: string, files: File[]): Promise<{ ok: number; failed: number; errors: { name: string; reason: string }[] }> {
  let ok = 0;
  let failed = 0;
  const errors: { name: string; reason: string }[] = []; // DQ-177: name + the server's reason per failed file
  for (const f of files) {
    try {
      await api.uploadTicketAttachment(ticketId, f);
      ok += 1;
    } catch (err) {
      failed += 1;
      errors.push({ name: f.name, reason: apiErrorMessage(err, '') });
    }
  }
  return { ok, failed, errors };
}

/**
 * A hidden multi-file input + `open()` (📎 in a row's ⋯ and the task page's ⋯).
 * The files go to the task as soon as they are picked.
 */
export function useAttachFiles(onDone?: (ticketId: string) => void) {
  const { t } = useTranslation();
  const ref = useRef<HTMLInputElement>(null);
  const target = useRef<string | null>(null);
  const [busy, setBusy] = useState(false);
  const open = (ticketId: string) => {
    target.current = ticketId;
    ref.current?.click();
  };
  const onPick = async (e: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = '';
    const id = target.current;
    if (!id || files.length === 0) return;
    setBusy(true);
    const r = await uploadTaskFiles(id, files);
    setBusy(false);
    if (r.ok) toast('success', t('taskFiles.uploaded', { count: r.ok }));
    if (r.failed) {
      const named = r.errors.filter((e) => e.reason);
      if (named.length === r.failed) named.forEach((e) => toast('error', t('documents.explorer.uploadFailedNamed', { name: e.name, reason: e.reason })));
      else toast('error', t('taskFiles.uploadFailed', { count: r.failed }));
    }
    onDone?.(id);
  };
  const input = (
    <input ref={ref} type="file" multiple accept={TASK_FILE_ACCEPT} className="hidden" onChange={onPick} data-testid="task-attach-input" aria-hidden="true" tabIndex={-1} />
  );
  return { open, input, busy };
}

/**
 * D-44 — the "קבצים" area inside the task page's "פרטים": the task's files
 * (ticket_attachments), upload of several, download, delete (staff).
 */
export function TaskFilesSection({ ticketId, canEdit }: { ticketId: string; canEdit: boolean }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['ticket-timeline', ticketId],
    queryFn: () => api.getTicketTimeline(ticketId),
  });
  const files: TicketAttachment[] = (data?.data?.ticket_attachments ?? []) as TicketAttachment[];
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['ticket-timeline', ticketId] });
  const attach = useAttachFiles(refresh);
  const [deleting, setDeleting] = useState<TicketAttachment | null>(null);
  const del = useMutation({
    mutationFn: (id: string) => api.deleteAttachment(ticketId, id),
    onSuccess: () => { setDeleting(null); refresh(); },
    onError: (err) => toast('error', apiErrorMessage(err, t('taskFiles.deleteFailed'))),
  });

  return (
    <section aria-labelledby="task-files-title" className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <h3 id="task-files-title" className="text-sm font-semibold text-text">{t('taskFiles.title')}</h3>
        {canEdit && (
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => attach.open(ticketId)} disabled={attach.busy} aria-busy={attach.busy || undefined}>
            <Paperclip size={14} aria-hidden="true" /> {t('taskFiles.upload')}
          </button>
        )}
      </div>
      {attach.input}
      {isLoading ? (
        <p className="text-sm text-text-2">{t('common.loading')}</p>
      ) : files.length === 0 ? (
        <p className="text-sm text-text-2">{t('taskFiles.none')}</p>
      ) : (
        <ul className="divide-y divide-border rounded-lg border border-border" data-testid="task-files-list">
          {files.map((f) => (
            <li key={f.id} className="flex items-center gap-3 px-3 py-2">
              <Paperclip size={14} className="shrink-0 text-text-2" aria-hidden="true" />
              <a href={(f as any).url || `/storage/attachments/${f.stored_filename}`} target="_blank" rel="noreferrer" className="min-w-0 flex-1 truncate text-sm text-text hover:underline" download={f.original_name}>
                <bdi>{f.original_name}</bdi>
              </a>
              <span className="text-xs text-text-2 tabular-nums">{sizeText(Number(f.file_size_bytes ?? 0))}</span>
              {f.created_at && <span className="hidden text-xs text-text-2 sm:inline">{formatDate(f.created_at)}</span>}
              <a href={(f as any).url || `/storage/attachments/${f.stored_filename}`} download={f.original_name} className="btn-icon text-text-2 hover:text-text" aria-label={t('taskFiles.download', { name: f.original_name })}>
                <Download size={14} aria-hidden="true" />
              </a>
              {canEdit && (
                <button type="button" className="btn-icon text-text-2 hover:text-danger-700" aria-label={t('taskFiles.delete', { name: f.original_name })} onClick={() => setDeleting(f)}>
                  <Trash2 size={14} aria-hidden="true" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      <ConfirmDialog
        open={!!deleting}
        title={t('taskFiles.deleteTitle')}
        description={deleting ? t('taskFiles.deleteConfirm', { name: deleting.original_name }) : undefined}
        confirmLabel={t('taskFiles.deleteAction')}
        danger
        loading={del.isPending}
        onConfirm={() => deleting && del.mutate(deleting.id)}
        onCancel={() => setDeleting(null)}
      />
    </section>
  );
}
